'use client';

import { Textarea } from '@workspace/ui/components/textarea';
import { cn } from '@workspace/ui/lib/utils';
import { TEXTAREA_MAX_LENGTH } from '@/src/constants/constants';
import { editProfile } from '@/src/actions/profile';
import { CharacterCounter, useToast } from '@/src/components/ui';
import { useEditableTextarea } from '@/src/hooks/use-editable-textarea';

type BioProps = {
  initialBio?: string;
  isMyProfile?: boolean;
  isMobileView?: boolean;
};

export const Bio = ({ initialBio, isMyProfile, isMobileView }: BioProps) => {
  const toast = useToast();
  const {
    tempValue,
    value: bio,
    isEditing,
    inputRef: rawInputRef,
    handleChange,
    handleKeyDown,
    submitEdit,
    startEdit,
  } = useEditableTextarea({
    initialValue: initialBio || '',
    onSubmit: async (newBio) => {
      try {
        toast.updating('Updating bio');
        await editProfile({ bio: newBio });
        toast.updated();
      } catch (error) {
        console.error('Failed to update bio:', error);
        toast.error('Failed to update bio');
      }
    },
  });

  const inputRef = rawInputRef as React.RefObject<HTMLTextAreaElement>;

  return (
    <div
      className={cn('w-full p-0 md:px-2 px-3', {
        'pb-5': isMyProfile,
      })}
    >
      {isMyProfile && isEditing ? (
        <>
          <Textarea
            ref={inputRef}
            maxLength={TEXTAREA_MAX_LENGTH}
            value={tempValue}
            onChange={handleChange}
            onBlur={submitEdit}
            onKeyDown={handleKeyDown}
            className="w-full border-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 text-black placeholder:text-gray-text placeholder:font-light placeholder:text-sm text-sm p-0 md:placeholder:text-[1rem] md:text-base break-words"
            placeholder="Your bio..."
          />
          <CharacterCounter
            className="border-t-1 pt-3 px-2 w-full"
            value={tempValue}
            maxLength={TEXTAREA_MAX_LENGTH}
          />
        </>
      ) : (
        <p
          className={cn(
            'text-black text-sm min-h-[43px] h-full max-h-96 overflow-ellipsis overflow-hidden break-all whitespace-pre-line',
            {
              'cursor-pointer': isMyProfile,
              'text-gray-text font-light': !bio,
              'md:text-base': !isMobileView,
            }
          )}
          onClick={isMyProfile ? startEdit : undefined}
        >
          {bio || (isMyProfile ? 'Your bio...' : 'No bio yet')}
        </p>
      )}
    </div>
  );
};
